import classNames from 'classnames';
import { forwardRef, useCallback, useEffect, useImperativeHandle } from 'react';

import { Alert, Checkbox } from '@zooplus/ui-components';

import styles from '../BancontactAdyenCreditCardForm/BancontactAdyenCreditCardForm.module.scss';

import { BANCONTACT_RENDER_CONTAINER_ID } from '@components/AdyenCreditCardForm/AdyenCreditCardForm.static';
import CardNumberFormField from '@components/CardNumberFormFieldNew/CardNumberFormFieldNew';
import DetectedCardBrandsController from '@components/DetectedCardBrandsController/DetectedCardBrandsController';
import FormControl from '@components/FormControl/FormControl';
import FormControlWrapper from '@components/FormControlWrapper/FormControlWrapper';
import SupportedCardList from '@components/SupportedCardList/SupportedCardList';
import useAdyenSecureFields from '@hooks/useAdyenSecureFields';
import useBancontactAdyenState from '@hooks/useBancontactAdyenState';
import { BancontactAdyenCreditCardFormData } from '@hooks/useBancontactAdyenState.static';
import useIsMobile from '@hooks/useIsMobile';
import { useTranslation } from '@hooks/useTranslations';
import { useAppContext } from '@providers/AppContextProvider';
import { CreditCardBrand, Nullable, Sizes } from '@types-internal';
import { AdyenConfig, adyenBrandOfCreditCardBrand } from '@types-internal/AdyenTypes';
import { exposeGlobally } from '@utils/utils';
import '@adyen/adyen-web/dist/adyen.css';

export type BancontactAdyenCreditCardFormRef = {
  computeBancontactAdyenCreditCardFormData: () => Nullable<BancontactAdyenCreditCardFormData>;
};

export interface BancontactAdyenCreditCardFormProps extends AdyenConfig {
  allowVaulting: boolean;
  errorMessage?: string;
  onFatalError: (error: unknown, hideForm?: boolean) => void;
}

const supportedCreditCardBrands: CreditCardBrand[] = ['bcmc'];
/**
 * The UI to add a new credit card, the security is handled by Adyen.
 * Relies on the hook 'useAdyenState' (to capture the data from Adyen and the unmanaged inputs) and 'useAdyenSecureFields' (to initialize the Adyen 'securedfields').
 *
 * Captured data can be retrieved with the globally-exposed 'computeBancontactAdyenCreditCardFormData'.
 */
const BancontactAdyenCreditCardForm = forwardRef<
  BancontactAdyenCreditCardFormRef,
  BancontactAdyenCreditCardFormProps
>(({ adyenEnvironment, clientKey, errorMessage, allowVaulting, onFatalError }, forwardedRef) => {
  const { t } = useTranslation();
  const { locale } = useAppContext();
  const isMobile = useIsMobile(Sizes.MD);
  const {
    detectedCardBrands,
    onChange,
    onFieldValid,
    onHolderNameChange,
    storeInVault,
    onStoreInVaultChange,
    focusedElement,
    onFocus,
    creditCardBrand,
    onBrand,
    onBinLookup,
    computeBancontactAdyenCreditCardFormData,
    errors,
  } = useBancontactAdyenState(onFatalError);

  const secureFields = useAdyenSecureFields(
    {
      clientKey,
      locale,
      t,
      containerId: BANCONTACT_RENDER_CONTAINER_ID,
      adyenEnvironment,
      onChange,
      onFocus,
      onFieldValid,
      onBrand,
      onBinLookup,
      onFatalError,
    },
    supportedCreditCardBrands,
  );

  // Expose the captured state globally so that external code can get the Adyen encrypted data
  useEffect(
    () =>
      exposeGlobally(
        'computeBancontactAdyenCreditCardFormData',
        computeBancontactAdyenCreditCardFormData,
      ),
    [computeBancontactAdyenCreditCardFormData],
  );

  // Expose the computeBancontactAdyenCreditCardFormData method internally too
  useImperativeHandle(forwardedRef, () => ({
    computeBancontactAdyenCreditCardFormData,
  }));

  /* Handle the card brand selection in case of co-branded */
  const onDualBrandingSelected = useCallback(
    (brand: CreditCardBrand) => {
      const adyenBrand = adyenBrandOfCreditCardBrand[brand];
      if (secureFields && adyenBrand) {
        secureFields.dualBrandingChangeHandler(adyenBrand);
      }
    },
    [secureFields],
  );

  const noCardBrandDetected = !creditCardBrand;
  const cardBrandDetected = !!creditCardBrand;

  const detectedBrandController = (
    <DetectedCardBrandsController
      detectedCardBrands={detectedCardBrands}
      selectedBrand={creditCardBrand}
      onBrandSelected={onDualBrandingSelected}
    />
  );

  return (
    <div
      id={BANCONTACT_RENDER_CONTAINER_ID}
      className={styles.adyenCreditCardForm}
      data-zta="bancontact-adyen-cc-form-container">
      {errorMessage && (
        <Alert
          className={styles.errorMessage}
          variant="warning"
          data-zta="adyen-credit-card-form-alert">
          {errorMessage}
        </Alert>
      )}
      <div className={styles.supportedCardsBar}>
        {noCardBrandDetected && <SupportedCardList cardBrands={supportedCreditCardBrands} />}
        {cardBrandDetected && isMobile && <>{detectedBrandController}</>}
      </div>
      <CardNumberFormField
        id="encryptedCardNumber"
        dataZta="bancontactNumber"
        detectedBrandsController={isMobile ? undefined : detectedBrandController}
        errorMessage={errors.encryptedCardNumber}
        focused={focusedElement === 'encryptedCardNumber'}
      />
      <FormControlWrapper
        label={t('checkout.payment.creditcard.holder_name')}
        labelFor="holderName"
        errorMessage={errors.holderName}
        errorZta="errorInputCardName">
        <input
          onChange={onHolderNameChange}
          className={classNames(
            'z-text-input',
            'z-input',
            styles.holderName,
            errors.holderName !== null && 'z-text-input--error',
          )}
          type="text"
          name="cc-name"
          autoComplete="cc-name"
          id="holderName"
          data-zta="bancontactHolderName"
          aria-invalid={errors.holderName !== null}
        />
      </FormControlWrapper>

      <FormControlWrapper
        label={t('checkout.payment.creditcard.expiry_date')}
        labelFor="encryptedExpiryDate"
        errorMessage={errors.encryptedExpiryDate}
        errorZta="errorInputCardDate">
        <div
          id="encryptedExpiryDate"
          className={classNames(styles.notFullWidth, styles.expiryDateContainer)}>
          <FormControl
            className={styles.fullWidth}
            innerClassname={classNames(styles.noPadding)}
            id="encryptedExpiryMonth"
            data-cse="encryptedExpiryMonth"
            hasError={errors.encryptedExpiryDate !== null}
            focused={focusedElement === 'encryptedExpiryMonth'}
            data-zta="bancontactMonth"
          />
          <span>/</span>
          <FormControl
            className={styles.fullWidth}
            innerClassname={classNames(styles.noPadding)}
            id="encryptedExpiryYear"
            data-cse="encryptedExpiryYear"
            hasError={errors.encryptedExpiryDate !== null}
            focused={focusedElement === 'encryptedExpiryYear'}
            data-zta="bancontactYear"
          />
        </div>
      </FormControlWrapper>

      {allowVaulting && (
        <Checkbox
          className={styles.vaultCheck}
          id="vault-check"
          checked={storeInVault}
          onChange={onStoreInVaultChange}
          data-zta="storeCreditCard">
          {t('checkout.payment.save.creditcard')}
        </Checkbox>
      )}
    </div>
  );
});

export default BancontactAdyenCreditCardForm;
